/**
 * Pure query-string builder for the listings search API (SS-2.7 + BUG-020).
 *
 * Extracted to its own module so it can be unit-tested with `npx tsx`
 * without pulling in lib/api/client.ts (which evaluates import.meta.env at
 * import time and dies under plain Node).
 *
 * BUG-020: beds/baths are now range-aware. A development listing with
 * `beds=1, beds_max=3` includes 2-bed variants and must match a "2 bed"
 * filter. We translate `value` to:
 *   value BETWEEN beds AND COALESCE(beds_max, beds)
 * via PostgREST nested boolean operators.
 *
 * Because PostgREST collapses duplicate top-level `or=` query keys (only
 * one survives), location + beds range can't both live at the top level.
 * We collect every clause that contains a logical OR into `complex[]` and
 * combine them under a single top-level `and=(...)` when more than one is
 * present.
 */

/**
 * UI amenity / must-have keys → Supabase boolean column names.
 */
export const AMENITY_COLUMN_MAP: Record<string, string> = {
  pool: 'pool',
  gym: 'gym',
  security: 'security',
  garden: 'garden',
  balcony: 'balcony',
  garage: 'garage',
  airCon: 'air_con',
  furnished: 'furnished',
  fireplace: 'fireplace',
  titleReady: 'title_ready',
  borehole: 'borehole',
  nearTarmac: 'near_tarmac',
};

/**
 * SYND-22 — extra place names for a location tag (its hierarchy descendants),
 * or none. Built by lib/search/geoExpansion.ts from `geo_areas`; declared here
 * structurally so this module keeps no imports.
 */
export type LocationExpander = (tag: string) => readonly string[];

/**
 * SYND-22 — the most expanded names one search sends, across ALL its location
 * tags together (deduplicated). Each tag is already capped at 150 names by the
 * expander (GEO_EXPANSION_CAP in lib/search/geoExpansion.ts, the same number);
 * this caps the whole predicate, because the names are sent twice (`city` and
 * `neighborhood`) in both the list and the count request, and several broad
 * tags would otherwise add up to a URL a proxy can refuse. Tags expand in the
 * order the buyer added them; a tag whose new names would exceed the budget
 * is searched without expansion (its ILIKE alternatives are unchanged).
 */
export const LOCATION_EXPANSION_BUDGET = 150;

/**
 * A PostgreSQL array literal for a PostgREST array operator — `cs`, `ov`,
 * `ilike(any)` — at the top level or inside `or(...)`/`and(...)`.
 *
 * Every element is double-quoted, with `\` and `"` backslash-escaped, so
 * spaces and commas inside a name ("Athi River", "Kilimani, Nairobi") are data,
 * not separators. Elements containing `{` or `}` are dropped: PostgREST reads
 * an array literal inside a logic tree up to the first closing brace, so a
 * brace cannot be carried at all. Empty and duplicate elements are dropped.
 */
export function pgArrayLiteral(values: readonly string[]): string {
  const seen = new Set<string>();
  const items: string[] = [];
  for (const value of values) {
    if (typeof value !== 'string' || value === '' || /[{}]/.test(value) || seen.has(value)) continue;
    seen.add(value);
    items.push(`"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`);
  }
  return `{${items.join(',')}}`;
}

export interface SearchListingsFilters {
  locations?: string[];
  minPrice?: number;
  maxPrice?: number;
  propertyType?: string;
  /**
   * HOME-01 fix round 1 — `property_type` values, already in the database's
   * spelling (`['villa','bungalow']`). Takes precedence over `propertyType`
   * when present, and emits `property_type=in.(…)`.
   *
   * Added because a chip can select more than one type: "House & Villa" covers
   * `villa` AND `bungalow`, and the single-valued `propertyType` above could
   * not say so — which is why every bungalow on the site was unreachable from
   * the home page's type chips. `propertyType` is kept rather than replaced
   * because saved searches and the alert cron (supabase/functions/_shared/
   * buildAlertQuery.ts) still speak it, and this round does not touch Edge
   * Functions. See src/components/home/propertyTypes.ts for the mapping.
   */
  propertyTypes?: string[];
  /** Specific bedroom counts; value 5 means "5+". */
  beds?: number[];
  /** Specific bathroom counts; value 5 means "5+". */
  baths?: number[];
  /** Legacy: minimum bedrooms (gte). Used only when `beds` is not provided. */
  minBeds?: number;
  /** Legacy: minimum bathrooms (gte). Used only when `baths` is not provided. */
  minBaths?: number;
  /** UI amenity keys (e.g. ['pool','garage']). Mapped to boolean columns. */
  amenities?: string[];
}

/**
 * PostgREST inner expression for range-aware count matching.
 *
 * For value < 5 we want: `value BETWEEN beds AND COALESCE(beds_max, beds)`.
 * Split by whether the listing is single-config or a range so a 1-bed
 * single-config listing (beds=1, beds_max=NULL) doesn't get pulled in by a
 * "2 bed" filter (BUG-020 iter 2). 13 such listings exist on prod.
 *   single-config (beds_max IS NULL) → beds == v
 *   range (beds_max IS NOT NULL)     → beds <= v <= beds_max
 *
 * For value ≥ 5 ("5+") we check `COALESCE(beds_max, beds) >= value` so a
 * range listing whose upper bound covers 5+ matches.
 */
function buildCountRangeExpr(value: number, col: string, maxCol: string): string {
  if (value >= 5) {
    return `or(${maxCol}.gte.${value},and(${col}.gte.${value},${maxCol}.is.null))`;
  }
  return `or(and(${col}.eq.${value},${maxCol}.is.null),and(${col}.lte.${value},${maxCol}.gte.${value}))`;
}

/**
 * The `property_type` clause, plural-first.
 *
 * `propertyTypes` wins when present because it is the one that can be right:
 * it carries database values with no casing round trip, and it can name more
 * than one. `propertyType` stays as the legacy single-value path for saved
 * searches, and keeps its `'New Development' → new_development` normalisation
 * so nothing that already worked stops working.
 *
 * Returns null for "no type filter" — an empty `propertyTypes` array is not a
 * request to match nothing, it is a caller who has not filtered.
 */
function propertyTypeClause(filters: SearchListingsFilters): string | null {
  if (filters.propertyTypes && filters.propertyTypes.length > 0) {
    return `property_type=in.(${filters.propertyTypes.join(',')})`;
  }
  if (filters.propertyType) {
    return `property_type=eq.${filters.propertyType.toLowerCase().replace(/\s+/g, '_')}`;
  }
  return null;
}

/**
 * Every filter clause for a listings search, WITHOUT ordering or projection.
 *
 * Split out so the feed query and the exact-count query cannot drift: HOME-01's
 * fix round has the page print a server `count=exact` next to the rows it is
 * showing, and a counter measured with a different predicate than the list is
 * exactly the class of small wrongness that teaches a buyer not to trust the
 * page. One predicate, two projections.
 *
 * SYND-22 — `expand` adds the tags' hierarchy descendants (a county's towns and
 * neighbourhoods, a town's neighbourhoods) as exact, case-insensitive
 * `city.ilike(any)` / `neighborhood.ilike(any)` alternatives INSIDE the same
 * location OR — one deduplicated pair for all tags, within
 * LOCATION_EXPANSION_BUDGET. A union: every row the ILIKE matched before still
 * matches. Without an expander (or with no names) the clause is unchanged.
 */
function buildListingsPredicate(filters: SearchListingsFilters, expand?: LocationExpander): string[] {
  const top: string[] = ['status=eq.published'];
  // Clauses that contain a logical OR. Each is a pre-wrapped PostgREST
  // expression: `or(...)` or `and(...)`. Combined under a single top-level
  // `and=(...)` when more than one is present.
  const complex: string[] = [];

  // Location → fuzzy ILIKE across city / neighborhood / address / title, plus
  // the tags' hierarchy descendants when an expander knows them (SYND-22). All
  // of it is one OR, so the descendants of every tag go in ONE deduplicated
  // `city.ilike(any)` / `neighborhood.ilike(any)` pair after the ILIKEs, under
  // LOCATION_EXPANSION_BUDGET for the whole search.
  if (filters.locations && filters.locations.length > 0) {
    const locConds: string[] = [];
    const expanded = new Map<string, string>(); // lowercased → as stored
    for (const loc of filters.locations) {
      const esc = loc.replace(/'/g, "''");
      locConds.push(
        `city.ilike.*${esc}*`,
        `neighborhood.ilike.*${esc}*`,
        `address.ilike.*${esc}*`,
        `title.ilike.*${esc}*`
      );
      const fresh = new Map<string, string>();
      for (const name of expand ? expand(loc) : []) {
        const key = name.trim().toLowerCase();
        if (key && !expanded.has(key)) fresh.set(key, name);
      }
      if (fresh.size > 0 && expanded.size + fresh.size <= LOCATION_EXPANSION_BUDGET) {
        for (const [key, name] of fresh) expanded.set(key, name);
      }
    }
    if (expanded.size > 0) {
      const list = pgArrayLiteral([...expanded.values()]);
      if (list !== '{}') locConds.push(`city.ilike(any).${list}`, `neighborhood.ilike(any).${list}`);
    }
    complex.push(`or(${locConds.join(',')})`);
  }

  // Beds — range-aware.
  if (filters.beds && filters.beds.length > 0) {
    const exprs = filters.beds.map(v => buildCountRangeExpr(v, 'beds', 'beds_max'));
    complex.push(exprs.length === 1 ? exprs[0] : `or(${exprs.join(',')})`);
  } else if (filters.minBeds) {
    top.push(`beds=gte.${filters.minBeds}`);
  }

  // Baths — range-aware.
  if (filters.baths && filters.baths.length > 0) {
    const exprs = filters.baths.map(v => buildCountRangeExpr(v, 'baths', 'baths_max'));
    complex.push(exprs.length === 1 ? exprs[0] : `or(${exprs.join(',')})`);
  } else if (filters.minBaths) {
    top.push(`baths=gte.${filters.minBaths}`);
  }

  if (filters.minPrice) top.push(`price=gte.${filters.minPrice}`);
  if (filters.maxPrice) top.push(`price=lte.${filters.maxPrice}`);

  const typeClause = propertyTypeClause(filters);
  if (typeClause) top.push(typeClause);

  if (filters.amenities && filters.amenities.length > 0) {
    for (const key of filters.amenities) {
      const col = AMENITY_COLUMN_MAP[key];
      if (col) top.push(`${col}=eq.true`);
    }
  }

  // Promote the complex clauses to top-level filters.
  if (complex.length === 1) {
    // Single complex clause — strip its outer wrapper and use as `or=(...)`
    // or `and=(...)` at the top level.
    const c = complex[0];
    if (c.startsWith('or(')) {
      top.push(`or=${c.slice(2)}`);
    } else if (c.startsWith('and(')) {
      top.push(`and=${c.slice(3)}`);
    } else {
      top.push(c);
    }
  } else if (complex.length > 1) {
    // Multiple complex clauses — nest under a single top-level `and=(...)`.
    top.push(`and=(${complex.join(',')})`);
  }

  return top;
}

export function buildSearchListingsQuery(filters: SearchListingsFilters, expand?: LocationExpander): string {
  return [...buildListingsPredicate(filters, expand), 'order=created_at.desc', 'select=*'].join('&');
}

/**
 * HOME-01 fix round 1 — the same search, asked as a count.
 *
 * Paired with `Prefer: count=exact`, which puts the total in the response's
 * `Content-Range` header. `limit=1` because the rows are not wanted: the
 * caller is asking how many there are, and downloading them to find out is how
 * "1,642" became `array.length` of whatever happened to be loaded.
 */
export function buildSearchListingsCountQuery(filters: SearchListingsFilters, expand?: LocationExpander): string {
  return [...buildListingsPredicate(filters, expand), 'select=id', 'limit=1'].join('&');
}

/**
 * SS-2.8: companion query for the "filtered nearby area counts" empty
 * state. Builds the same range-aware filter clauses as
 * `buildSearchListingsQuery` (BUG-020) but:
 *  - constrains rows to an explicit `neighborhood IN (...)` list rather
 *    than a fuzzy location ILIKE
 *  - selects only the `neighborhood` column (the caller aggregates counts
 *    client-side)
 *  - omits ordering
 *
 * The location filter from `filters` is intentionally ignored — the
 * neighborhoods array is the location constraint.
 */
export function buildFilteredAreaCountsQuery(
  neighborhoods: string[],
  filters: Omit<SearchListingsFilters, 'locations'>,
): string {
  const top: string[] = ['status=eq.published'];
  const complex: string[] = [];

  if (filters.beds && filters.beds.length > 0) {
    const exprs = filters.beds.map(v => buildCountRangeExpr(v, 'beds', 'beds_max'));
    complex.push(exprs.length === 1 ? exprs[0] : `or(${exprs.join(',')})`);
  } else if (filters.minBeds) {
    top.push(`beds=gte.${filters.minBeds}`);
  }

  if (filters.baths && filters.baths.length > 0) {
    const exprs = filters.baths.map(v => buildCountRangeExpr(v, 'baths', 'baths_max'));
    complex.push(exprs.length === 1 ? exprs[0] : `or(${exprs.join(',')})`);
  } else if (filters.minBaths) {
    top.push(`baths=gte.${filters.minBaths}`);
  }

  if (filters.minPrice) top.push(`price=gte.${filters.minPrice}`);
  if (filters.maxPrice) top.push(`price=lte.${filters.maxPrice}`);

  const typeClause = propertyTypeClause(filters);
  if (typeClause) top.push(typeClause);

  if (filters.amenities && filters.amenities.length > 0) {
    for (const key of filters.amenities) {
      const col = AMENITY_COLUMN_MAP[key];
      if (col) top.push(`${col}=eq.true`);
    }
  }

  if (complex.length === 1) {
    const c = complex[0];
    if (c.startsWith('or(')) top.push(`or=${c.slice(2)}`);
    else if (c.startsWith('and(')) top.push(`and=${c.slice(3)}`);
    else top.push(c);
  } else if (complex.length > 1) {
    top.push(`and=(${complex.join(',')})`);
  }

  if (neighborhoods.length > 0) {
    // PostgREST `in.(...)` — wrap values containing commas/spaces/parens in
    // double quotes. Internal quotes are doubled per PostgREST CSV rules.
    const formatted = neighborhoods
      .map(n => {
        const needsQuote = /[ ,()"]/.test(n);
        const escaped = n.replace(/"/g, '""');
        return needsQuote ? `"${escaped}"` : escaped;
      })
      .join(',');
    top.push(`neighborhood=in.(${formatted})`);
  }

  top.push('select=neighborhood');
  return top.join('&');
}
